import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { EstimatePostingService } from './estimate-posting.service';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams
} from '../../../shared/workspace-element-context';

interface MeCodeItem {
  ME_CODE: string;
  ME_NAME: string;
  selected: boolean;
}

@Component({
    selector: 'gtw-estimate-posting',
    templateUrl: './estimate-posting.component.html',
    styleUrls: ['./estimate-posting.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EstimatePostingComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  baseURLs: any = { api: '' };
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  userSettings: any = {};

  minimise = false;
  loadingMeCodes = false;
  saveloader = false;
  deleteLoader = false;
  recalcLoader = false;

  meCodeList: MeCodeItem[] = [];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private estimatePostingService: EstimatePostingService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.userSettings = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized || this.loadingMeCodes) return;
    if (!this.GENERAL_CONFIG || !this.filterParams || Object.keys(this.filterParams).length === 0) return;
    const baseUrl = this.GENERAL_CONFIG.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    this.isInitialized = true;
    this.loadMeCodes();
  }

  private loadMeCodes(): void {
    this.loadingMeCodes = true;
    this.cdr.detectChanges();
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    const params: any = _.merge(
      {},
      this.globalService?.globalParams ?? this.globalService ?? {},
      this.filterParams ?? {}
    );
    params.action_code = 'i2wznw';
    if (!params.tax_year) params.tax_year = this.filterParams?.tax_year ?? this.globalService?.tax_year;
    if (!params.jcd_key) params.jcd_key = this.globalService?.jcd_key ?? this.filterParams?.jcd_key ?? 250;
    if (!params.scenario) params.scenario = this.filterParams?.scenario ?? this.globalService?.scenario;

    this.estimatePostingService.getMeCodes(baseUrl, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.loadingMeCodes = false;
        if (data?.callSuccess === '1' && data.jsonObject && Array.isArray(data.jsonObject)) {
          this.meCodeList = data.jsonObject.map((item: any) => ({
            ME_CODE: item.VALUE || item.value || '',
            ME_NAME: item.LABEL || item.label || (item.VALUE || item.value || ''),
            selected: true
          })).filter((x: MeCodeItem) => x.ME_CODE);
          this.meCodeList.sort((a, b) => (a.ME_CODE || '').localeCompare(b.ME_CODE || ''));
        } else {
          this.meCodeList = [];
        }
        this.cdr.detectChanges();
      },
      () => {
        this.loadingMeCodes = false;
        this.meCodeList = [];
        this.cdr.detectChanges();
      }
    );
  }

  selectAllMeCodes(): void {
    this.meCodeList.forEach(m => m.selected = true);
    this.cdr.detectChanges();
  }

  unselectAllMeCodes(): void {
    this.meCodeList.forEach(m => m.selected = false);
    this.cdr.detectChanges();
  }

  getSelectedMeCodesCount(): number {
    return this.meCodeList.filter(m => m.selected).length;
  }

  getSelectedMeCodes(): string[] {
    return this.meCodeList.filter(m => m.selected).map(m => m.ME_CODE);
  }

  isAllSelected(): boolean {
    return this.meCodeList.length > 0 && this.meCodeList.every(m => m.selected);
  }

  isNoneSelected(): boolean {
    return this.meCodeList.length === 0 || this.meCodeList.every(m => !m.selected);
  }

  private buildParams(): any {
    const selected = this.getSelectedMeCodes();
    let params: any = _.merge({}, this.globalService?.globalParams ?? this.globalService ?? {}, this.filterParams ?? {});
    params.me_le = selected.join(',');
    return params;
  }

  estimatePosting(): void {
    if (this.getSelectedMeCodesCount() === 0) {
      this.gtwSnackbarService.openSnackBar('Please select at least one ME Code', 'gtw-snackbar--yellow');
      return;
    }
    this.saveloader = true;
    this.cdr.detectChanges();
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    this.estimatePostingService.estimatePosting(baseUrl, this.buildParams()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.saveloader = false;
        this.cdr.detectChanges();
        if (data?.callSuccess === '1' && data?.errorMessage === 'null') {
          this.gtwSnackbarService.openSnackBar('Estimated Posting saved successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Error saving', 'gtw-snackbar--red');
        }
      },
      () => {
        this.saveloader = false;
        this.cdr.detectChanges();
        this.gtwSnackbarService.openSnackBar('Error saving estimate posting', 'gtw-snackbar--red');
      }
    );
  }

  deletePostingsFromEST(): void {
    if (this.getSelectedMeCodesCount() === 0) {
      this.gtwSnackbarService.openSnackBar('Please select at least one ME Code', 'gtw-snackbar--yellow');
      return;
    }
    this.deleteLoader = true;
    this.cdr.detectChanges();
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    this.estimatePostingService.deletePostingsFromEST(baseUrl, this.buildParams()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.deleteLoader = false;
        this.cdr.detectChanges();
        if (result?.errorMessage && result.errorMessage !== 'null') {
          if (result?.callSuccess === '2') {
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--yellow');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          }
        } else {
          this.gtwSnackbarService.openSnackBar('Postings from EST deleted successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
      },
      () => {
        this.deleteLoader = false;
        this.cdr.detectChanges();
        this.gtwSnackbarService.openSnackBar('Error deleting postings from EST', 'gtw-snackbar--red');
      }
    );
    this.gtwSnackbarService.openSnackBar('Delete and Recalculation in progress. Please verify after some time.', 'gtw-snackbar--yellow');
    this.requestGridUpdate.emit('cancel');
  }

  recalculateTotals(): void {
    if (this.getSelectedMeCodesCount() === 0) {
      this.gtwSnackbarService.openSnackBar('Please select at least one ME Code', 'gtw-snackbar--yellow');
      return;
    }
    this.recalcLoader = true;
    this.cdr.detectChanges();
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    this.estimatePostingService.recalculateTotals(baseUrl, this.buildParams()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.recalcLoader = false;
        this.cdr.detectChanges();
        if (result?.errorMessage && result.errorMessage !== 'null') {
          if (result?.callSuccess === '2') {
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--yellow');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          }
        } else {
          this.gtwSnackbarService.openSnackBar('Postings from EST recalculated successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
      },
      () => {
        this.recalcLoader = false;
        this.cdr.detectChanges();
      }
    );
    this.gtwSnackbarService.openSnackBar('Recalculation in progress. Please verify after some time.', 'gtw-snackbar--yellow');
    this.requestGridUpdate.emit('cancel');
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
